import { ExtensionType } from '../../../extensions/Extensions.mjs';

"use strict";
class GpuBindGroupEntry {
  constructor(gpuBindGroup, now) {
    this.autoGarbageCollect = true;
    /** Part of the {@link GCable} contract; a cached bind group owns no per-renderer GPU data. */
    this._gpuData = null;
    this.gpuBindGroup = gpuBindGroup;
    this._gcLastUsed = now;
  }
  /**
   * Native bind groups have no destroy; dropping the reference is the whole release. Anything
   * still holding the group (a recorded render bundle, a graphics batch) keeps it alive, and the
   * next immediate-mode bind simply recreates the cache entry.
   */
  unload() {
    this.gpuBindGroup = null;
  }
}
class BindGroupSystem {
  constructor(renderer) {
    this._hash = /* @__PURE__ */ Object.create(null);
    this._renderer = renderer;
    renderer.gc.addResourceHash(this, "_hash", "resource");
    renderer.gc.addCollection(this, "_hash", "hash");
  }
  contextChange(gpu) {
    this._gpu = gpu;
    this._hash = /* @__PURE__ */ Object.create(null);
  }
  getBindGroup(bindGroup, program, groupIndex) {
    const key = `${bindGroup._key}:${program._layoutKey << 4 | groupIndex}`;
    const entry = this._hash[key];
    if (entry) {
      entry._gcLastUsed = this._renderer.gc.now;
      return entry.gpuBindGroup;
    }
    return this._createBindGroup(key, bindGroup, program, groupIndex);
  }
  _createBindGroup(key, group, program, groupIndex) {
    const device = this._gpu.device;
    const groupLayout = program.layout[groupIndex];
    const entries = [];
    const renderer = this._renderer;
    for (const j in groupLayout) {
      const resource = group.resources[j] ?? group.resources[groupLayout[j]];
      if (!resource || resource.destroyed) {
        throw new Error(`[BindGroup] the resource bound as '${j}' was destroyed while a shader still uses it. Remove it from the shader before destroying it.`);
      }
      let gpuResource;
      if (resource._resourceType === "uniformGroup") {
        const uniformGroup = resource;
        renderer.ubo.updateUniformGroup(uniformGroup);
        const buffer = uniformGroup.buffer;
        gpuResource = {
          buffer: renderer.buffer.getGPUBuffer(buffer),
          offset: 0,
          size: buffer.descriptor.size
        };
      } else if (resource._resourceType === "buffer") {
        const buffer = resource;
        gpuResource = {
          buffer: renderer.buffer.getGPUBuffer(buffer),
          offset: 0,
          size: buffer.descriptor.size
        };
      } else if (resource._resourceType === "bufferResource") {
        const bufferResource = resource;
        gpuResource = {
          buffer: renderer.buffer.getGPUBuffer(bufferResource.buffer),
          offset: bufferResource.offset,
          size: bufferResource.size ?? bufferResource.buffer.descriptor.size
        };
      } else if (resource._resourceType === "textureSampler") {
        const sampler = resource;
        gpuResource = renderer.texture.getGpuSampler(sampler);
      } else if (resource._resourceType === "textureSource") {
        const texture = resource;
        gpuResource = renderer.texture.getTextureView(texture);
      } else if (resource._resourceType === "textureView") {
        const textureView = resource;
        gpuResource = renderer.texture.getTextureView(textureView.source, textureView.viewDescriptor);
      }
      entries.push({
        binding: groupLayout[j],
        resource: gpuResource
      });
    }
    const layout = renderer.shader.getProgramData(program).bindGroups[groupIndex];
    const gpuBindGroup = device.createBindGroup({
      layout,
      entries
    });
    this._hash[key] = new GpuBindGroupEntry(gpuBindGroup, renderer.gc.now);
    return gpuBindGroup;
  }
  destroy() {
    this._hash = null;
    this._renderer = null;
  }
}
/** @ignore */
BindGroupSystem.extension = {
  type: [
    ExtensionType.WebGPUSystem
  ],
  name: "bindGroup"
};

export { BindGroupSystem };
//# sourceMappingURL=BindGroupSystem.mjs.map
